import React from 'react';
import { TouchableOpacity, Text, StyleSheet, ActivityIndicator, Platform } from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { useTheme, SPACING, BORDER_RADIUS, TYPOGRAPHY, GOOGLE_LOGO } from '../../theme';
import { useWebHover } from '../../hooks/useWebHover';
import { CURSOR_POINTER, CURSOR_DISABLED } from '../../utils/webStyles';

interface GoogleSignInButtonProps {
  onPress: () => void;
  loading?: boolean;
  disabled?: boolean;
  label?: string;
  testID?: string;
}

/**
 * "Continue with Google" CTA. Mirrors Button's `lg` size and web hover
 * affordances so it drops in wherever the email form's primary submit
 * button lives, but keeps Google's own brand colors (googleButton* tokens)
 * instead of the app's evergreen palette — this button is a recognizable
 * third-party mark, not a REDI CTA.
 */
export function GoogleSignInButton({
  onPress,
  loading = false,
  disabled = false,
  label = 'Continue with Google',
  testID,
}: GoogleSignInButtonProps) {
  const { colors } = useTheme();
  const isInert = disabled || loading;
  const { hovered, hoverProps } = useWebHover(!isInert);

  // Web-only by design: sign-in here is Firebase's popup-based web SDK flow
  // (signInWithPopup/linkWithPopup); there's no native equivalent yet, so
  // this renders nothing on iOS/Android.
  if (Platform.OS !== 'web') return null;

  return (
    <TouchableOpacity
      onPress={onPress}
      disabled={isInert}
      activeOpacity={0.8}
      testID={testID}
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityState={{ disabled: isInert, busy: loading }}
      {...hoverProps}
      style={[
        styles.button,
        {
          backgroundColor: colors.googleButtonBackground,
          borderColor: colors.googleButtonBorder,
        },
        hovered && !isInert && { backgroundColor: colors.hoverSurface },
        disabled && styles.disabled,
        disabled ? CURSOR_DISABLED : CURSOR_POINTER,
      ]}>
      {loading ? (
        <ActivityIndicator color={colors.googleButtonText} size="small" />
      ) : (
        <>
          <Svg width={20} height={20} viewBox="0 0 48 48" style={styles.logo}>
            <Path
              fill={GOOGLE_LOGO.blue}
              d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"
            />
            <Path
              fill={GOOGLE_LOGO.green}
              d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"
            />
            <Path
              fill={GOOGLE_LOGO.yellow}
              d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88z"
            />
            <Path
              fill={GOOGLE_LOGO.red}
              d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z"
            />
          </Svg>
          <Text style={[styles.text, { color: colors.googleButtonText }]}>{label}</Text>
        </>
      )}
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    paddingVertical: 16,
    // Pill to match the Plaud button grammar (Google's guidelines allow it)
    borderRadius: BORDER_RADIUS.full,
    borderWidth: 1,
  },
  disabled: {
    opacity: 0.5,
  },
  logo: {
    marginRight: SPACING.sm,
  },
  text: {
    ...TYPOGRAPHY.button,
  },
});
